import type {Dispatch} from 'redux';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword
} from 'firebase/auth';
import {auth, db, FirebaseTimestamp} from '../../firebase';
import {collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, setDoc} from "firebase/firestore";
import {
  fetchFavoriteProductsAction,
  fetchProductsInCartAction,
  resetCartAction,
  signInAction,
  signOutAction,
  updateCartAction,
  updateUserInfoAction,
} from './actions';
import {RootState} from '../store/store';
import {ProductInCart} from './types';
import toast from 'react-hot-toast';
import { NavigateFunction } from 'react-router-dom';



// ユーザー情報を更新する
export const updateUserInfo = (firstNameKana: string, lastNameKana: string, email: string, phone: string, address: string, gender :string, birthdayYear: string, birthdayMonth: string, navigate: NavigateFunction) => {
  
  return async (dispatch: Dispatch, getState: () => RootState) => {
    function isZenkakuKana(s: string): boolean {
      return !!s.match(/^[ァ-ヶー　]*$/);
    }

    // validation
    if (lastNameKana === '' || firstNameKana === '' || email === '' || phone === "") {
      toast.error('必須項目が未入力です');
      return;
    }
    if (!isZenkakuKana(lastNameKana) || !isZenkakuKana(firstNameKana)) {
      toast.error('名前はカタカナで入力してください');
      return;
    }
    if (phone.length !== 11){
      toast.error('携帯電話番号が正しく入力されているかお確かめください');
      return;
    }

    const uid = getState().users.uid;

    const userRef = doc(db, 'users', uid);

    const data = {
      firstNameKana,
      lastNameKana,
      email,
      phone,
      address,
      gender,
      birthdayYear,
      birthdayMonth,
    }

    // 新しいユーザー情報をデータベースにセット
    await setDoc(userRef, data, { merge: true });

    // ユーザーの情報を取得
    getDoc(userRef).then((snapshot) => {
      const userData = snapshot.data();
      if (userData) {
        // ストアの中身を更新
        dispatch(updateUserInfoAction({
          address: userData.address,
          birthdayYear: userData.birthdayYear,
          birthdayMonth: userData.birthdayMonth,
          cart: userData.cart,
          email: userData.email,
          firstNameKana: userData.firstNameKana,
          gender: userData.gender,
          isSignedIn: true,
          lastNameKana: userData.lastNameKana,
          orders: userData.orders,
          phone: userData.phone,
          role: userData.role,
          uid: uid,
        }))
      }
    });

    navigate('/user/my-page');
    toast.success('アカウント情報を保存しました');
  }
}

// お気に入りから商品を削除する
export const removeFavorite = (id: string) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const uid = getState().users.uid;

    const favoriteRef = doc(db, 'users', uid, 'favorite', id);
    // データベースから削除
    deleteDoc(favoriteRef).then(() => {
      // const prevFavorite = getState().users.favorite;
      // // 削除した商品以外で、お気に入りリストを更新
      // const newFavorite = prevFavorite.filter((product: any) => product.id !== id);
      // dispatch(deleteFavoriteAction(newFavorite));
    });

  }
}

// お気に入りリストに商品を追加する
export const addProductToFavorite = (data: any) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const uid = getState().users.uid;
    // 'favorite' : ユーザーの持つサブコレクション
    const favoriteRef = doc(collection(db, 'users', uid, 'favorite'));

    const product = {
      ...data,
      id: favoriteRef.id
    }
    await setDoc(favoriteRef, product);
    toast.success('お気に入りに追加しました');
  }
}

// お気に入りリストを取得する
export const fetchFavoriteProducts = () => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const uid = getState().users.uid;
    const list: Array<any> = [];

    const favoriteRef = collection(db, 'users', uid, 'favorite');
    const q = query(favoriteRef, orderBy('added_at', 'desc'));
    getDocs(q).then((snapshots) => {
      snapshots.forEach((snapshot) => {
        const data = snapshot.data();
        list.push(data);
      })
      dispatch(fetchFavoriteProductsAction(list));
    });
  }
}

// ショッピングカートに商品を増やす
export const addProductToCart = (data: Omit<ProductInCart, "quantity" >, prevCart: Array<ProductInCart>) => {
  
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const index = prevCart.findIndex(
      (product: ProductInCart) => product.id === data.id
    );
    if(index === -1) {
      prevCart.push({...data, "quantity" : 1});
    } else {
      prevCart[index].quantity = prevCart[index].quantity + 1
    }
    dispatch(updateCartAction(prevCart));
  }
}

// ショッピングカートに商品を減らす
export const subtractProductFromCart = (data: Omit<ProductInCart, "quantity" >, prevCart: Array<ProductInCart>) => {
  
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const index = prevCart.findIndex(
      (product: ProductInCart) => product.id === data.id
    );
    if(index === -1) {
      
    } else {
      if(prevCart[index].quantity > 0)prevCart[index].quantity = prevCart[index].quantity - 1
    }
    dispatch(updateCartAction(prevCart));
  }
}
export const removeProductFromCart = (data: Omit<ProductInCart, "quantity" >, prevCart: Array<ProductInCart>) => {
  
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const index = prevCart.findIndex(
      (product: ProductInCart) => product.id === data.id
    );
    if(index === -1) {
      
    } else {
      prevCart[index].quantity = 0;
    }
    dispatch(updateCartAction(prevCart));
  }
}
export const resetCart = () => {
  
  return async (dispatch: Dispatch) => {
    dispatch(resetCartAction());
  }
}

// リスナーで監視したカート情報をストアに反映
export const fetchProductsInCart = (products: Array<ProductInCart>) => {
  return async (dispatch: Dispatch) => {
    dispatch(fetchProductsInCartAction(products));
  }
}

// 認証をリッスン(監視)する
// 前回のセッションが残っていれば、自動でstoreに認証情報を追加して認証後画面に遷移
export const listenAuthState = (navigate: NavigateFunction) => {
  
  return async (dispatch: Dispatch) => {
    return onAuthStateChanged(auth, (user) => {
      if (user) {
        // ユーザーがログインしている場合
        const uid = user.uid;
        // ユーザーの情報を取得
        getDoc(doc(db, "users", uid)).then((snapshot) => {
          const userData = snapshot.data();
          if (userData) {
            // ストアの中身を更新
            dispatch(signInAction({
              address: userData.address,
              birthdayYear: userData.birthdayYear,
              birthdayMonth: userData.birthdayMonth,
              cart: userData.cart,
              email: userData.email,
              firstNameKana: userData.firstNameKana,
              gender: userData.gender,
              isSignedIn: true,
              lastNameKana: userData.lastNameKana,
              orders: userData.orders,
              phone: userData.phone,
              role: userData.role,
              uid: uid,
            }))
          }
        });
      } else {
        // ユーザーがログインしていない場合、ログインページに飛ぶ
        navigate('/signin');
      }
    });
  }
}

export const resetPassword = (email: string, navigate: NavigateFunction) => {
  return async (dispatch: Dispatch) => {
    // validation
    if (email === '') {
      toast.error('必須項目が未入力です');
      return;
    }
    sendPasswordResetEmail(auth, email).then(() => {
      toast.success('入力されたアドレスにパスワードリセット用のメールを送信しました。');
      navigate('/signin');
    }).catch(() => {
      toast.error('パスワードリセットに失敗しました。通信状態を確認してください。');
    })
  }
}

// ログインを行う
export const signInOperation = (email: string, password: string, navigate: NavigateFunction) => {
  return async (dispatch: Dispatch) => {
    // validation
    if (email === '' || password === '') {
      toast.error('必須項目が未入力です');
      return;
    }
    signInWithEmailAndPassword(auth, email, password).then((result) => {
      const user = result.user;
      if (user) {
        const uid = user.uid;
        // ユーザーの情報を取得
        getDoc(doc(db, "users", uid)).then((snapshot) => {
          const userData = snapshot.data();
          if (userData) {
            // ストアの中身を更新
            dispatch(signInAction({
              address: userData.address,
              birthdayYear: userData.birthdayYear,
              birthdayMonth: userData.birthdayMonth,
              cart: userData.cart,
              email: userData.email,
              firstNameKana: userData.firstNameKana,
              gender: userData.gender,
              isSignedIn: true,
              lastNameKana: userData.lastNameKana,
              orders: userData.orders,
              phone: userData.phone,
              role: userData.role,
              uid: uid,
            }))
            navigate('/');
          }
        });
        toast.success('ログインしました');
      }
    }).catch(() => {
      toast.error('メールアドレス、またはパスワードが間違っています。');
    });
  }
}
// アカウント登録を行う
export const signUpOperation = (lastNameKana: string, firstNameKana: string, email: string, password: string, confirmPassword: string, phone: string, birthdayYear :string, birthdayMonth :string, gender: string,  address: string, navigate: NavigateFunction) => {
  return async (dispatch: Dispatch) => {

    function isZenkakuKana(s: string): boolean {
      return !!s.match(/^[ァ-ヶー　]*$/);
    }

    // validation
    if (lastNameKana === '' || firstNameKana === '' || email === '' || password === '' || confirmPassword === '' || phone === "") {
      toast.error('必須項目が未入力です');
      return;
    }
    if (!isZenkakuKana(lastNameKana) || !isZenkakuKana(firstNameKana)) {
      toast.error('名前はカタカナで入力してください');
      return;
    }
    if (phone.length !== 11){
      toast.error('携帯電話番号が正しく入力されているかお確かめください');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('パスワードが一致しません。もう一度お試しください');
      return;
    };
    if (password.length < 6) {
      toast.error('パスワードは6文字以上で入力してください');
      return;
    }

    return createUserWithEmailAndPassword(auth, email, password).then((result) => {
      const user = result.user;
      // アカウント登録が成功したら、ユーザー情報をFirestoreに保存する
      if (user) {
        const uid = user.uid;
        // 登録した時刻
        const timestamp = FirebaseTimestamp.now();

        const userInitialDate = {
          created_at: timestamp,
          email: email,
          role: 'customer',
          uid: uid,
          updated_at: timestamp,
          lastNameKana: lastNameKana,
          firstNameKana: firstNameKana,
          phone: phone,
          birthdayYear: birthdayYear,
          birthdayMonth: birthdayMonth,
          gender: gender,
          address: address

        }

        // ユーザー情報をデータベースに登録する
        setDoc(doc(db, 'users', uid), userInitialDate).then(() => {
          navigate('/');
        });
        toast.success('新規登録が完了しました');
      }
    }).catch((error) => {
      if (error.message === 'Firebase: Error (auth/email-already-in-use).') {
        toast.error('新規登録に失敗しました。既に登録されているメールアドレスです。');
      }
      if (error.message === 'Firebase: Error (auth/invalid-email).') {
        toast.error('新規登録に失敗しました。不正な形式のメールアドレスです。');
      }
      if (error.message === 'Firebase: Password should be at least 6 characters (auth/weak-password).') {
        toast.error('新規登録に失敗しました。パスワードは6文字以上で入力してください。');
      }
    })
  }
}

// ログアウトする
export const signOut = (navigate: NavigateFunction) => {
  return async (dispatch: Dispatch) => { 
    auth.signOut().then(() => {
      dispatch(signOutAction());
      navigate('/signin');
      toast.success('ログアウトしました');
    })
  }
}